/**
 * Centralized SEO configuration and head metadata management.
 * Uses the VITE_SITE_URL environment variable for the canonical origin.
 */

export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://allchinabuyspread.com';

export const SITE_NAME = 'AllChinaBuy Spreadsheet';

// User-provided favicon / brand logo (from the authoritative embed_code).
export const LOGO_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

// Existing absolute images already used across the project.
const HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=Beautiful%20aerial%20view%20of%20luxury%20fashion%20boutique%20interior%20with%20clothing%20racks%20and%20shoe%20displays%2C%20warm%20soft%20lighting%2C%20cream%20and%20beige%20color%20palette%2C%20editorial%20fashion%20photography%20style%2C%20minimalist%20elegant%20composition%2C%20high%20end%20retail%20aesthetic&width=1920&height=1080&seq=hero-bg-01&orientation=landscape';
const IMG_GUIDE =
  'https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20of%20fashion%20shopping%20essentials%20with%20laptop%20spreadsheet%20on%20white%20marble%20desk%2C%20sneakers%20clothing%20accessories%20arranged%20neatly%2C%20bright%20natural%20light%2C%20clean%20editorial%20photography%20style%2C%20soft%20shadows%2C%20warm%20neutral%20tones&width=800&height=500&seq=article-guide-01&orientation=landscape';
const IMG_REVIEW =
  'https://readdy.ai/api/search-image?query=Person%20unboxing%20fashion%20package%20on%20wooden%20table%2C%20sneakers%20and%20clothing%20items%20spilling%20out%2C%20bright%20daylight%20from%20window%2C%20clean%20minimalist%20photography%20style%2C%20warm%20neutral%20color%20palette%2C%20editorial%20product%20photography&width=800&height=500&seq=article-review-01&orientation=landscape';
const IMG_SHIPPING =
  'https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20stacked%20neatly%20in%20warehouse%20with%20soft%20natural%20light%2C%20clean%20modern%20logistics%20space%2C%20minimalist%20photography%20style%2C%20warm%20neutral%20tones%2C%20white%20and%20beige%20color%20palette%2C%20editorial%20photography&width=800&height=500&seq=article-shipping-01&orientation=landscape';
const IMG_COUPON =
  'https://readdy.ai/api/search-image?query=Hands%20holding%20smartphone%20showing%20discount%20coupon%20app%20screen%2C%20shopping%20bags%20in%20background%20soft%20focus%2C%20bright%20clean%20studio%20lighting%2C%20minimalist%20photography%20style%2C%20warm%20neutral%20tones%2C%20lifestyle%20editorial%20photography&width=800&height=500&seq=article-coupon-01&orientation=landscape';
const IMG_VS =
  'https://readdy.ai/api/search-image?query=Two%20smartphones%20side%20by%20side%20on%20white%20marble%20surface%20showing%20shopping%20app%20interfaces%2C%20minimalist%20comparison%20visual%2C%20clean%20bright%20lighting%2C%20soft%20shadows%2C%20neutral%20warm%20tones%2C%20editorial%20product%20photography&width=800&height=500&seq=article-vs-01&orientation=landscape';
const IMG_SAFETY =
  'https://readdy.ai/api/search-image?query=Secure%20lock%20icon%20with%20shopping%20cart%20on%20clean%20white%20background%2C%20minimalist%203D%20render%20style%2C%20soft%20pastel%20colors%2C%20modern%20security%20concept%20visual%2C%20warm%20neutral%20tones%2C%20clean%20product%20photography%20style&width=800&height=500&seq=article-safety-01&orientation=landscape';

export interface SeoData {
  /** Route path, "/" for home. */
  path: string;
  title: string;
  description: string;
  keywords: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE = HERO_IMAGE;

const SEO_ROUTES: Record<string, SeoData> = {
  '/': {
    path: '/',
    title: 'AllChinaBuy Spreadsheet - Find Products, Compare & Shop Smarter',
    description:
      'AllChinaBuy Spreadsheet resource hub. Explore curated product spreadsheets, reviews, shipping guides, QC tips, and comparisons. Your trusted resource for smarter shopping from China.',
    keywords:
      'AllChinaBuy, spreadsheet, shopping agent, China shopping, reviews, shipping costs, QC guide, coupons, discount codes',
    ogTitle: 'AllChinaBuy Spreadsheet - Find Products, Compare & Shop Smarter',
    ogDescription:
      'Explore AllChinaBuy spreadsheet resources, product guides, shipping information, reviews and comparisons.',
    ogType: 'website',
    ogImage: HERO_IMAGE,
  },
  '/allchinabuy-spreadsheet-complete-guide': {
    path: '/allchinabuy-spreadsheet-complete-guide',
    title: 'AllChinaBuy Spreadsheet Complete Guide 2026: Find Products & Shop Smarter',
    description:
      'The complete AllChinaBuy Spreadsheet guide covering product categories, shipping costs, QC photos, coupons, and agent comparisons. Learn how to use spreadsheets to shop smarter in 2026.',
    keywords:
      'AllChinaBuy spreadsheet, complete guide, product categories, shipping costs, QC guide, coupons, shopping agent',
    ogTitle: 'AllChinaBuy Spreadsheet Complete Guide 2026',
    ogDescription:
      'Everything you need to know about AllChinaBuy spreadsheet resources, product categories, shipping, QC, and smart shopping tips.',
    ogType: 'article',
    ogImage: IMG_GUIDE,
  },
  '/allchinabuy-review': {
    path: '/allchinabuy-review',
    title: 'AllChinaBuy Review 2026: Features, Fees & User Experience',
    description:
      'In-depth AllChinaBuy review covering features, fee structure, shipping options, quality check service, customer support, and pros and cons for 2026.',
    keywords: 'AllChinaBuy review, AllChinaBuy fees, AllChinaBuy features, shopping agent review, 2026',
    ogTitle: 'AllChinaBuy Review 2026: Features, Fees & User Experience',
    ogDescription:
      'Comprehensive AllChinaBuy review covering features, fees, shipping options, customer support, and real user experiences.',
    ogType: 'article',
    ogImage: IMG_REVIEW,
  },
  '/allchinabuy-shipping-cost': {
    path: '/allchinabuy-shipping-cost',
    title: 'AllChinaBuy Shipping Cost: Complete Guide to Rates & Savings',
    description:
      'Complete guide to AllChinaBuy shipping costs covering methods, cost factors, delivery times, customs, and proven tips to reduce your international shipping expenses.',
    keywords: 'AllChinaBuy shipping cost, shipping methods, delivery time, customs, shipping savings',
    ogTitle: 'AllChinaBuy Shipping Cost: Complete Guide to Rates & Savings',
    ogDescription:
      'Complete breakdown of AllChinaBuy shipping methods, costs by country, and tips to reduce your shipping expenses.',
    ogType: 'article',
    ogImage: IMG_SHIPPING,
  },
  '/allchinabuy-coupon': {
    path: '/allchinabuy-coupon',
    title: 'AllChinaBuy Coupon 2026: Current Discounts & How to Save',
    description:
      'Find current AllChinaBuy coupons and discount codes. Learn about first-time user coupons, seasonal promotions, referral bonuses, and money-saving strategies.',
    keywords: 'AllChinaBuy coupon, discount code, promo code, savings, first-time coupon',
    ogTitle: 'AllChinaBuy Coupon 2026: Current Discounts & How to Save',
    ogDescription: 'Current AllChinaBuy coupons, discount codes, and money-saving tips for shoppers.',
    ogType: 'article',
    ogImage: IMG_COUPON,
  },
  '/allchinabuy-vs-superbuy': {
    path: '/allchinabuy-vs-superbuy',
    title: 'AllChinaBuy vs Superbuy vs CSSBuy vs Sugargoo: Complete Comparison',
    description:
      'Detailed comparison of AllChinaBuy against Superbuy, CSSBuy, and Sugargoo covering features, QC, shipping, fees, and interface to help you choose the right agent.',
    keywords: 'AllChinaBuy vs Superbuy, CSSBuy, Sugargoo, shopping agent comparison, fees',
    ogTitle: 'AllChinaBuy vs Superbuy vs CSSBuy vs Sugargoo: Complete Comparison',
    ogDescription:
      'Detailed comparison of AllChinaBuy with Superbuy, CSSBuy, Sugargoo, and Pandabuy.',
    ogType: 'article',
    ogImage: IMG_VS,
  },
  '/allchinabuy-legit-safety': {
    path: '/allchinabuy-legit-safety',
    title: 'Is AllChinaBuy Legit? Safety, Reviews & Buying Guide',
    description:
      'Is AllChinaBuy legit and safe? This guide covers payment security, warehouse QC, buyer protection, and practical tips for safe international shopping.',
    keywords: 'Is AllChinaBuy legit, AllChinaBuy safe, AllChinaBuy safety, buyer protection, QC',
    ogTitle: 'Is AllChinaBuy Legit? Safety, Reviews & Buying Guide',
    ogDescription:
      'Safety guide covering payment security, QC processes, warehouse storage, and how to avoid scams.',
    ogType: 'article',
    ogImage: IMG_SAFETY,
  },
  '/about': {
    path: '/about',
    title: 'About Us - AllChinaBuy Spreadsheet',
    description:
      'Learn about AllChinaBuy Spreadsheet, an independent resource hub helping shoppers navigate Chinese shopping agents with transparent reviews, guides, and comparisons.',
    keywords: 'about AllChinaBuy Spreadsheet, independent resource, shopping guides',
    ogTitle: 'About Us - AllChinaBuy Spreadsheet',
    ogDescription: 'Independent resource hub for AllChinaBuy reviews, guides, and comparisons.',
    ogType: 'website',
    ogImage: HERO_IMAGE,
  },
  '/contact': {
    path: '/contact',
    title: 'Contact Us - AllChinaBuy Spreadsheet',
    description:
      'Contact the AllChinaBuy Spreadsheet team with questions, suggestions, or collaboration opportunities. We typically respond within 24-48 hours.',
    keywords: 'contact AllChinaBuy Spreadsheet, questions, suggestions, collaboration',
    ogTitle: 'Contact Us - AllChinaBuy Spreadsheet',
    ogDescription: 'Reach out to the AllChinaBuy Spreadsheet team with questions or suggestions.',
    ogType: 'website',
    ogImage: HERO_IMAGE,
  },
  '/privacy': {
    path: '/privacy',
    title: 'Privacy Policy - AllChinaBuy Spreadsheet',
    description: 'Privacy Policy for AllChinaBuy Spreadsheet.',
    keywords: 'privacy policy',
    noindex: true,
  },
  '/terms': {
    path: '/terms',
    title: 'Terms of Service - AllChinaBuy Spreadsheet',
    description: 'Terms of Service for AllChinaBuy Spreadsheet.',
    keywords: 'terms of service',
    noindex: true,
  },
  '/disclaimer': {
    path: '/disclaimer',
    title: 'Disclaimer - AllChinaBuy Spreadsheet',
    description: 'Disclaimer for AllChinaBuy Spreadsheet.',
    keywords: 'disclaimer',
    noindex: true,
  },
};

export function getSeoData(path: string): SeoData {
  const normalized = path.replace(/\/+$/, '') || '/';
  return (
    SEO_ROUTES[normalized] || {
      path: normalized,
      title: SITE_NAME,
      description: 'AllChinaBuy Spreadsheet resource hub for smarter shopping from China.',
      keywords: 'AllChinaBuy, spreadsheet, shopping agent',
      ogTitle: SITE_NAME,
      ogDescription: 'AllChinaBuy Spreadsheet resource hub for smarter shopping from China.',
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
    }
  );
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Applies the resolved SEO data to document.title and the managed head tags.
 * Every managed value is (re)written on each call so metadata never leaks
 * between route transitions.
 */
export function applyHead(seo: SeoData): void {
  const url = seo.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${seo.path}`;
  const ogTitle = seo.ogTitle || seo.title;
  const ogDescription = seo.ogDescription || seo.description;
  const ogType = seo.ogType || 'website';
  const ogImage = seo.ogImage || DEFAULT_OG_IMAGE;

  document.title = seo.title;

  upsertMeta('name', 'description', seo.description);
  upsertMeta('name', 'keywords', seo.keywords);
  upsertMeta('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow');
  upsertLink('canonical', url);

  upsertMeta('property', 'og:title', ogTitle);
  upsertMeta('property', 'og:description', ogDescription);
  upsertMeta('property', 'og:type', ogType);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', ogImage);
  upsertMeta('property', 'og:site_name', SITE_NAME);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', ogTitle);
  upsertMeta('name', 'twitter:description', ogDescription);
  upsertMeta('name', 'twitter:image', ogImage);
}